import { useState, useCallback, useEffect, useRef } from 'react';
import { getJuthoorInitiativeStats } from '@/lib/api/juthoor';
import type { JuthoorInitiativeStats } from '@/types/juthoor';

export function useJuthoorInitiativeStats(initiativeId: string, enabled: boolean = true): {
  data: JuthoorInitiativeStats | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
} {
  const [data, setData] = useState<JuthoorInitiativeStats | null>(null);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchStats = useCallback(async () => {
    if (!enabled) return;
    try {
      setLoading(true);
      setError(null);
      const response = await getJuthoorInitiativeStats(initiativeId);
      if (!mountedRef.current) return;
      if (response.success && response.data) {
        setData(response.data);
      } else {
        setError(response.message || 'Failed to load initiative stats');
      }
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load initiative stats');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [initiativeId, enabled]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { data, loading, error, refetch: fetchStats };
}
